/**
 * maxCarsProjection — derives the largest car count an adapter's offsets can
 * serve before per-car expansion collides with itself.
 *
 * WHY THIS EXISTS. LN1's .CFB stores each car's offset per category
 * independently, and nothing forces two categories to walk at the same rate.
 * Categories with different strides eventually CROSS. On a real six-car site
 * car position walked 23 bytes per car from 131 while a control category walked
 * 16 from 168; they met at byte 200 — car 4's position register and car 3's
 * control base. LN2's offset_to_car_num map is keyed on the byte ALONE, so byte
 * 200 silently resolved to one car and that car reported the other's telemetry.
 * Car 3's dashboard tracked car 4. Nothing errored, because nothing looked.
 *
 * This module looks. It sweeps car counts upward and reports the first count at
 * which expansion would collide, so the arithmetic is visible and bounded
 * BEFORE it is expanded rather than discovered on a dashboard months later.
 *
 * THE CAP USES THE WIRE MODEL, AND ONLY THE WIRE MODEL. A cap is enforced by
 * LiftCloud, which drops every offset-mapping row for a car at or beyond it, and
 * LiftCloud's own collision detector buckets claims by the BASE BYTE and reports
 * one only when two DIFFERENT cars claim it (utils/flatbuffers/generation/
 * offset_collisions.go). So this module bounds the cap on exactly that: two
 * different cars, one base byte. Deriving from a wider model — overlapping
 * category EXTENTS, say — would auto-write a cap tighter than the thing the
 * server enforces, and the server would then delete cars whose collision it
 * cannot see or confirm. Extent overlap is still projected, because it is real
 * and the operator should know, but it is ADVISORY: reported, never capped on.
 *
 * TWO SOURCES, ONE MODEL. The authored matrix (initial + car delta per column)
 * is what an operator edits, but it is a lossy view: a mapping imported from a
 * .CFB enumerates categories the matrix has no column for, and the byte-200 pair
 * on the real site is two of them. So the sweep also runs over the ENUMERATED
 * offset mapping — byte for byte the table LiftCloud enforces on — and the
 * tighter of the two answers wins.
 *
 * WHY IT DOES NOT CALL detectMatrixOverlaps. That function answers a different
 * question — "does this fixed car count collide?" — and it unconditionally
 * skips pairs sharing a colKey (offsetOverlapDetection.ts, the `a.colKey ===
 * b.colKey` early return). A zero delta is the purest "expansion eventually
 * collides" shape and it is same-column, so it is invisible to that function at
 * any car count. Teaching it to see them would change shared detection
 * semantics; this module instead consumes its exported primitives.
 *
 * Pure TypeScript: no React, no antd, no FlatBuffer generated types.
 */
import {
  CategoryRegTypeMap,
  MatrixColumnSpec,
  ResolvedAddress,
  isGroupScopedCategory,
} from "./offsetOverlapDetection";

/**
 * Upper bound on the sweep. Comfortably above any real LN1 group bus, and it
 * keeps the search at roughly |series|² · ceiling² / 2 pair tests — a few
 * milliseconds inside a useMemo for a typical twenty-column adapter.
 *
 * It is also the ceiling the editor's input enforces and the ceiling LiftCloud's
 * adapter controller rejects past, on BOTH transports — the FlatBuffer bind path
 * skips gin's struct validator, so the range is checked in the controller rather
 * than claimed by a `binding` tag that path never runs.
 */
export const MAX_CARS_PROJECTION_CEILING = 64;

/**
 * The smallest cap auto-detection is willing to WRITE.
 *
 * A projection of one car says the offsets contest a byte as soon as a second
 * car exists. That is a defect in the offsets — two series laid down on top of
 * each other — and a cap of one is not its remedy; it is a multi-car site
 * demolished down to a single car by a background effect. The same reasoning
 * already refuses to derive a cap from a category with no per-car delta, which
 * is the identical shape at the identical remedy. Both are reported instead, and
 * an operator who genuinely wants one car can still type 1.
 */
export const MIN_AUTO_FILL_CARS = 2;

/** Wire offsets are uint32 on both sides; an address outside it cannot exist. */
const MAX_WIRE_BYTE = 0xffffffff;

/** One byte two different cars both claim, with both claimants named. */
export interface CarCollisionWitness {
  byte: number;
  a: ResolvedAddress;
  b: ResolvedAddress;
}

/**
 * Why expansion has to stop.
 *
 *   - "byte-contest": two different cars claim one base byte. This is the
 *     defect the feature exists for, and it is exactly what LiftCloud detects.
 *   - "out-of-range": a car's address walked outside the representable wire
 *     range. A negative car delta does this, and the offset matrix accepts one
 *     (its parser special-cases a lone "-" mid-typing), so it is reachable
 *     input rather than a hypothetical. Expansion cannot continue past it —
 *     models.OffsetMappingEntry.Offset is a uint32 — so it bounds the cap too.
 */
export type MaxCarsBoundKind = "byte-contest" | "out-of-range";

export interface MaxCarsBound {
  kind: MaxCarsBoundKind;
  /** Smallest car count whose expansion hits this bound. */
  carCount: number;
  /** The two contesting claims. Populated for "byte-contest" only. */
  witness: CarCollisionWitness | null;
  /** The address that left the wire range. Populated for "out-of-range" only. */
  address: ResolvedAddress | null;
}

/**
 * Two cars whose category EXTENTS overlap without contesting a base byte.
 *
 * Real, and worth saying — the two cars read overlapping regions of the frame —
 * but ADVISORY: the extent comes from the editor's key mappings and does not
 * exist in the offset table LiftCloud enforces on, so a cap derived from it
 * would delete cars on evidence the server cannot check.
 */
export interface MaxCarsExtentOverlap {
  carCount: number;
  witness: CarCollisionWitness;
}

/**
 * A configured column with no per-car delta, named twice on purpose.
 *
 * `colKey` is the IDENTITY — what dedup across sources keys on, and the only
 * thing two sources can agree about. `label` is what the operator reads, and the
 * matrix source has held it all along on `MatrixColumnSpec.label`: the caption
 * that names this defect was printing `traffic:normal:0` at somebody who has
 * only ever seen that column called "Position". Carrying both means neither the
 * dedup nor the sentence has to make do with the other's string.
 */
export interface ZeroDeltaColumn {
  colKey: string;
  label: string;
}

export interface MaxCarsProjection {
  /** Why expansion stops, or null when it is clean to the ceiling. */
  bound: MaxCarsBound | null;
  /** Smallest car count whose expansion is bounded; null ⇒ clean. */
  firstCollidingCarCount: number | null;
  /** Largest safe car count — firstColliding − 1. Null when that would be 0. */
  safeCarCount: number | null;
  /** Advisory extent overlap. Never bounds the cap. */
  extentOverlap: MaxCarsExtentOverlap | null;
  /** Configured columns whose per-car delta is 0 (a defect, but not a cap). */
  zeroDeltaColumns: ZeroDeltaColumn[];
}

/** The initial/delta pair an operator authors for one category column. */
export interface ColumnOffsetValue {
  initial: number;
  delta: number;
}

/**
 * One enumerated offset-mapping row, as the server stores it.
 *
 * Field names are the wire names deliberately: this is the same JSON the editor
 * hydrates from and the same shape Go unmarshals into models.OffsetMappingEntry,
 * so a row that reaches here is a row LiftCloud will enforce on.
 */
export interface OffsetMappingRow {
  message_type: number;
  message_value?: number;
  message_sub_type?: string;
  unit_number: number;
  offset: number;
}

/**
 * What a caller can say about one enumerated mapping row that the row cannot say
 * about itself.
 *
 * THE TWO SOURCES NAME THE SAME REGISTER IN TWO NOTATIONS. The authored matrix
 * keys a column `traffic:normal:0` and knows its label, its MessageContent
 * discriminant, its MODBUS register type and the operator's own shared-register
 * toggle. The enumerated mapping keys the identical register `7|P|` and knows
 * none of that — and worse, its `message_type` is not even in the same enum
 * namespace on a LEGACY payload, where 7 means Traffic and MessageContent 7
 * means Group Supervisory Status. Reading it as a MessageContent stamped the
 * six-car site's car-position series with the discriminant of a category it has
 * nothing to do with.
 *
 * Resolving that needs the category tables and the adapter's own columns, and
 * this module may not import either (see the header: no generated types, no
 * React). So the caller resolves and injects, exactly as `IntraContentLookup`
 * is injected into overlap detection.
 *
 * Every field is independently optional, because the honest answer for a
 * per-variant series the matrix has no column for is "I know the category, I do
 * not know the column" — and a resolver that had to invent the rest would put
 * the wrong register type and the wrong sharing policy on it to say so.
 */
export interface MappingSeriesIdentity {
  /** Matrix column key (`"traffic:normal:0"`), or null when no column matches. */
  colKey: string | null;
  /** True MessageContent discriminant, or null when the category is unknown. */
  mcNumber: number | null;
  /** Operator-facing name (`"Traffic: Position"`); null ⇒ keep the wire label. */
  label: string | null;
  /** Declared MODBUS register type, when a column supplied one. */
  regType?: string;
  /**
   * The matrix column's own shared-register verdict.
   *
   * Present ONLY when a column matched, and then it wins over the data
   * heuristic below — including an explicit `false`, which is an operator who
   * turned the group-shared toggle off and means the register flagged. Absent
   * for the per-variant series that have no column, which is the case the data
   * heuristic exists for.
   */
  sharedAcrossCars?: boolean;
}

/**
 * Resolve one mapping row to what the matrix knows about the same register.
 *
 * Returning null is a legitimate answer — "nothing about this row resolves" —
 * and leaves the series on the wire notation it already had.
 */
export type MappingSeriesResolver = (row: OffsetMappingRow) => MappingSeriesIdentity | null;

/**
 * A per-car address series the sweep walks.
 *
 * Both sources reduce to this, which is what lets one sweep — and therefore one
 * collision model — serve the authored matrix and the enumerated table alike.
 */
interface CarSeries {
  key: string;
  label: string;
  size: number;
  mcNumber: number;
  regType?: string;
  /**
   * One register read identically by every car. Exempt from SAME-series
   * cross-car comparison: a shared register "colliding" with itself on car 2 is
   * the register working, not a defect, and without this every adapter carrying
   * a Group Control column would project a cap of one car.
   */
  sharedAcrossCars: boolean;
  /** Absolute byte for `unitIdx`, or null when the series does not reach it. */
  addressAt: (unitIdx: number) => number | null;
}

const EMPTY_PROJECTION: MaxCarsProjection = {
  bound: null,
  firstCollidingCarCount: null,
  safeCarCount: null,
  extentOverlap: null,
  zeroDeltaColumns: [],
};

// ---------------------------------------------------------------------------
// The collision model
// ---------------------------------------------------------------------------

/**
 * A column is EXEMPT from same-column cross-car comparison when its one
 * register is legitimately shared by every car. These are exactly the three
 * guards detectBankMatrixOverlaps applies, kept in the same order so the two
 * agree by inspection:
 *
 *   1. bankLevel — explicitly a whole-bank register, no per-car delta;
 *   2. groupShared — a group-supervisory / group-control register, defaulting
 *      on for group-scoped categories but operator-overridable (an explicit
 *      false re-enables flagging);
 *   3. isByteExpansion — a ByteMessage floor expansion, where the value varies
 *      per floor while the offset stays fixed.
 *
 * Exported because the ENUMERATED source has to reach the same verdict about
 * the same register. It used to guess from the data instead, which meant an
 * operator's explicit `groupShared: false` re-enabled flagging in the matrix and
 * was invisible in the mapping, so one adapter answered the question two ways.
 */
export function isSharedAcrossCars(col: MatrixColumnSpec): boolean {
  if (col.bankLevel === true) return true;
  if (col.isByteExpansion === true) return true;

  return col.groupShared ?? isGroupScopedCategory(col.colKey);
}

/**
 * Register types that are declared AND different never share a wire byte —
 * a MODBUS coil at address 200 is not the holding register at 200. Mirrors the
 * identical guard in detectMatrixOverlaps.
 */
function regTypesExclude(a: ResolvedAddress, b: ResolvedAddress, byKey: Map<string, CarSeries>) {
  const ra = byKey.get(a.colKey)?.regType;
  const rb = byKey.get(b.colKey)?.regType;

  return Boolean(ra && rb && ra !== rb);
}

/**
 * May these two claims be compared at all?
 *
 * ONLY CROSS-CAR PAIRS EVER REACH HERE, and that is a property of the sweep
 * rather than a test repeated in this function: every address in one frontier
 * belongs to a single car, and a frontier is only ever compared against earlier
 * cars (see firstPair). The property matters, because one car sharing a register
 * between two categories is a legitimate configuration — that is what the
 * intra-content flags describe — and the offset→car map stays unambiguous
 * through it, since it answers only "which car". LiftCloud agrees by
 * construction: its detector reports a byte only when the claims on it span two
 * cars. Two identical series therefore bound nothing, which is pinned.
 *
 * What is left to decide here is the pair of exemptions that are NOT structural:
 * a shared register compared against itself on a later car, and two declared,
 * differing MODBUS register types.
 */
function comparable(
  a: ResolvedAddress,
  b: ResolvedAddress,
  byKey: Map<string, CarSeries>,
): boolean {
  if (a.colKey === b.colKey && byKey.get(a.colKey)?.sharedAcrossCars === true) return false;

  return !regTypesExclude(a, b, byKey);
}

/** The wire model: one base byte, two cars. Exactly what offset_to_car_num keys on. */
function contestsByte(a: ResolvedAddress, b: ResolvedAddress, byKey: Map<string, CarSeries>) {
  return comparable(a, b, byKey) && a.absByte === b.absByte;
}

/** Advisory model: half-open ranges [start, start+size) touch, base bytes differ. */
function overlapsExtent(a: ResolvedAddress, b: ResolvedAddress, byKey: Map<string, CarSeries>) {
  if (a.absByte === b.absByte) return false;

  return (
    comparable(a, b, byKey) && a.absByte < b.absByte + b.size && b.absByte < a.absByte + a.size
  );
}

/** The contested byte is the first one both claims cover. */
function witnessFor(a: ResolvedAddress, b: ResolvedAddress): CarCollisionWitness {
  return { byte: Math.max(a.absByte, b.absByte), a, b };
}

// ---------------------------------------------------------------------------
// The sweep
// ---------------------------------------------------------------------------

function resolve(series: CarSeries, unitIdx: number): ResolvedAddress | null {
  const absByte = series.addressAt(unitIdx);
  if (absByte === null) return null;

  return {
    unitIdx,
    absByte,
    bit: -1,
    colKey: series.key,
    label: series.label,
    size: series.size,
    mcNumber: series.mcNumber,
  };
}

/**
 * First pair between the new frontier and an earlier car satisfying `hit`.
 *
 * Only `fresh × prior` is searched, and that is not an optimisation: every
 * address in one frontier belongs to the SAME car, and every rule here requires
 * two different cars, so a within-frontier pair can never match. Searching it
 * anyway would be dead code pretending to be coverage.
 *
 * Stops at the first match: the sweep is O(series² · ceiling²) and must not
 * degrade into scanning every remaining pair once the answer is known.
 */
function firstPair(
  fresh: ResolvedAddress[],
  prior: ResolvedAddress[],
  hit: (a: ResolvedAddress, b: ResolvedAddress) => boolean,
): CarCollisionWitness | null {
  for (let i = 0; i < fresh.length; i += 1) {
    const earlier = prior.find((candidate) => hit(fresh[i], candidate));
    if (earlier) return witnessFor(fresh[i], earlier);
  }

  return null;
}

/**
 * Sweep car counts upward and report the first count that is bounded.
 *
 * INCREMENTAL FRONTIER. At each car count N only the newly added addresses
 * (car index N−1) can create a collision, because the pair rules do not depend
 * on N and growing N only ADDS addresses. So each step tests the new frontier
 * against itself and against everything accumulated, rather than re-running full
 * detection over all N² pairs at every step.
 *
 * MONOTONE, therefore well-defined: a larger N can never REMOVE a collision, so
 * the first bounded count is a genuine threshold rather than an artefact of the
 * order in which pairs happen to be visited.
 *
 * The advisory extent scan runs alongside, and stops with the cap: an extent
 * overlap at a car the cap already excludes is noise, while one at or before the
 * capped count is something the operator needs to hear.
 */
function sweep(series: CarSeries[], ceiling: number, zeroDeltaColumns: ZeroDeltaColumn[]) {
  const byKey = new Map(series.map((s) => [s.key, s]));
  const accumulated: ResolvedAddress[] = [];

  let bound: MaxCarsBound | null = null;
  let extentOverlap: MaxCarsExtentOverlap | null = null;

  for (let carCount = 1; carCount <= ceiling && bound === null; carCount += 1) {
    const frontier = series
      .map((s) => resolve(s, carCount - 1))
      .filter((addr): addr is ResolvedAddress => addr !== null);

    const escaped = frontier.find((a) => a.absByte < 0 || a.absByte > MAX_WIRE_BYTE);
    if (escaped) {
      bound = { kind: "out-of-range", carCount, witness: null, address: escaped };
    }

    if (bound === null) {
      const contest = firstPair(frontier, accumulated, (a, b) => contestsByte(a, b, byKey));
      if (contest) bound = { kind: "byte-contest", carCount, witness: contest, address: null };
    }

    if (extentOverlap === null) {
      const touch = firstPair(frontier, accumulated, (a, b) => overlapsExtent(a, b, byKey));
      if (touch) extentOverlap = { carCount, witness: touch };
    }

    accumulated.push(...frontier);
  }

  return {
    bound,
    firstCollidingCarCount: bound === null ? null : bound.carCount,
    safeCarCount: bound === null || bound.carCount <= 1 ? null : bound.carCount - 1,
    extentOverlap,
    zeroDeltaColumns,
  };
}

// ---------------------------------------------------------------------------
// Source 1 — the authored offset matrix
// ---------------------------------------------------------------------------

/**
 * Columns worth projecting: enabled, and actually configured.
 *
 * A column left wholly unset (initial 0 AND delta 0) is EXCLUDED. detectMatrix-
 * Overlaps defaults missing values to {initial: 0, delta: 0}, so including them
 * would pile every unconfigured category onto byte 0 and project a cap of one
 * car for every half-built adapter in the fleet.
 */
function projectableColumns(
  columns: MatrixColumnSpec[],
  values: Record<string, ColumnOffsetValue>,
): Array<{ col: MatrixColumnSpec; value: ColumnOffsetValue }> {
  return columns
    .filter((col) => !col.disabled)
    .map((col) => ({ col, value: values[col.colKey] ?? { initial: 0, delta: 0 } }))
    .filter(({ value }) => value.initial !== 0 || value.delta !== 0);
}

/**
 * Configured columns with no per-car delta.
 *
 * A genuine defect — every car would read the same register — and under the wire
 * model it bounds the cap at one car all by itself, which auto-fill then
 * refuses. Naming the column is far more use to the operator than naming the
 * byte, so it is reported alongside.
 */
function findZeroDeltaColumns(
  projectable: Array<{ col: MatrixColumnSpec; value: ColumnOffsetValue }>,
): ZeroDeltaColumn[] {
  return projectable
    .filter(({ col, value }) => value.delta === 0 && !isSharedAcrossCars(col))
    .map(({ col }) => ({ colKey: col.colKey, label: col.label }));
}

/**
 * Project from the offset matrix an operator authors: initial byte plus a
 * per-car delta, one pair per category column.
 *
 * This is the FORWARD-looking source. It answers "how far can these strides
 * walk", including past the car count the adapter currently enumerates, which is
 * the question an operator raising the car count is really asking.
 */
export function projectMaxCars(
  columns: MatrixColumnSpec[],
  values: Record<string, ColumnOffsetValue>,
  regTypes?: CategoryRegTypeMap,
  ceiling: number = MAX_CARS_PROJECTION_CEILING,
): MaxCarsProjection {
  const projectable = projectableColumns(columns, values);
  const zeroDeltaColumns = findZeroDeltaColumns(projectable);

  if (projectable.length === 0) return { ...EMPTY_PROJECTION, zeroDeltaColumns };

  const series: CarSeries[] = projectable.map(({ col, value }) => ({
    key: col.colKey,
    label: col.label,
    size: Math.max(1, col.size),
    mcNumber: col.mcNumber,
    regType: regTypes?.[col.colKey],
    sharedAcrossCars: isSharedAcrossCars(col),
    addressAt: (unitIdx: number) => value.initial + unitIdx * value.delta,
  }));

  return sweep(series, ceiling, zeroDeltaColumns);
}

// ---------------------------------------------------------------------------
// Source 2 — the enumerated offset mapping
// ---------------------------------------------------------------------------

/**
 * Series identity, matching how the mapping enumerates one category's cars.
 *
 * `?? ""` and not `|| ""`: a `message_value` of 0 is a real enum member — the
 * first option of every category — and an ABSENT value means a grouped
 * category-wide series instead. `5|normal|0` and `5|normal|` are two registers,
 * and `||` would key them alike and let one overwrite the other's offsets. The
 * distinction survives the server round trip because
 * `models.OffsetMappingEntry.MsgValue` is a `*int`, and `omitempty` on a pointer
 * drops nil only, never a pointer to zero.
 */
function seriesKeyOf(row: OffsetMappingRow): string {
  return `${row.message_type}|${row.message_sub_type ?? ""}|${row.message_value ?? ""}`;
}

/**
 * How a mapping series is named when nothing resolved it.
 *
 * Deliberately the same shape LiftCloud's collision report uses
 * (OffsetCarClaim.String, "message_type 7:normal:-"), so the byte named in the
 * editor and the byte named in the server log read as the same finding rather
 * than as two unrelated complaints about the same adapter.
 *
 * It is the FALLBACK, though, not the format: an operator reading "message_type
 * 7:P:-" has to know both that 7 is the importer's Traffic rather than the
 * schema's, and that P is Position, to learn what the editor already knows. A
 * resolved series is named "Traffic: Position" instead — see
 * `MappingSeriesResolver`.
 */
function seriesLabel(key: string): string {
  const [type, subtype, value] = key.split("|");

  return `message_type ${type}:${subtype || "normal"}:${value || "-"}`;
}

/**
 * One enumerated series: its offsets by car, and a row that stands for it.
 *
 * The representative row is kept because identity resolution is a question
 * about the (type, subtype, value) triple, not about any one car's offset, and
 * every row in a series carries the same triple by construction.
 */
interface GroupedSeries {
  byUnit: Map<number, number>;
  sample: OffsetMappingRow;
}

/**
 * Group by the WIRE TRIPLE, and never by the resolved column key.
 *
 * The matrix folds a category's variants into one column by taking the maximum
 * initial and delta across them, so two distinct wire series routinely resolve
 * to the SAME `colKey`. Keying the grouping on the resolved key would merge
 * them, and a merge here is not a cosmetic loss: `byUnit.set` would let one
 * series' offsets overwrite the other's car for car, and the sweep would then
 * walk a series that exists on no wire at all. Identity stays the triple;
 * resolution is attached to it, never substituted for it.
 */
function groupRowsBySeries(rows: readonly OffsetMappingRow[]): Map<string, GroupedSeries> {
  const grouped = new Map<string, GroupedSeries>();

  rows.forEach((row) => {
    if (!Number.isFinite(row.offset) || !Number.isFinite(row.unit_number)) return;

    const key = seriesKeyOf(row);
    const entry = grouped.get(key) ?? { byUnit: new Map<number, number>(), sample: row };
    entry.byUnit.set(row.unit_number, row.offset);
    grouped.set(key, entry);
  });

  return grouped;
}

/**
 * Project from the ENUMERATED offset mapping — the table LiftCloud enforces on.
 *
 * This source exists because the authored matrix is a lossy view of it. The
 * matrix carries one column per category the editor knows about and folds a
 * category's variants together by taking the maximum initial and delta across
 * them; a mapping imported from a .CFB enumerates variants the matrix has no
 * column for at all. On the real six-car site the byte-200 pair is two such
 * variants, so a projection that reads only the matrix cannot see the very
 * collision the feature is named after.
 *
 * SHARED SERIES ARE READ OUT OF THE DATA, not out of a column flag: a series
 * whose offset is the same for every car is one register the whole bank reads,
 * so it is exempt from same-series cross-car comparison exactly as a bank-level
 * column is. It is still compared against OTHER series, because a walking series
 * arriving on a shared register's byte is a real contest that a cap does fix.
 *
 * An all-zero series is dropped as unconfigured, for the same reason the matrix
 * source drops an unset column.
 *
 * WHAT `resolve` BUYS, and why the answer is not "a nicer label". Without it
 * this source reads `message_type` as a MessageContent discriminant, and on a
 * LEGACY payload it simply is not one: the .CFB importer's 7 is Traffic while
 * MessageContent 7 is Group Supervisory Status, so the six-car site's
 * car-position series was stamped with the discriminant of an unrelated
 * category. Three further facts only the matrix holds were unreachable for the
 * same reason — the declared MODBUS register type (`regTypesExclude` could never
 * fire here, because `CategoryRegTypeMap` is keyed by column key and these
 * series keys are not column keys), the operator's explicit `groupShared: false`
 * (which the matrix honours and this source could only guess at from the data),
 * and the column's human label.
 */
export function projectMaxCarsFromOffsetMapping(
  rows: readonly OffsetMappingRow[] | null | undefined,
  resolveIdentity?: MappingSeriesResolver,
  ceiling: number = MAX_CARS_PROJECTION_CEILING,
): MaxCarsProjection {
  if (!Array.isArray(rows) || rows.length === 0) return EMPTY_PROJECTION;

  const series: CarSeries[] = [];

  groupRowsBySeries(rows).forEach(({ byUnit, sample }, key) => {
    const offsets = [...byUnit.values()];
    if (offsets.every((offset) => offset === 0)) return;

    const identity = resolveIdentity?.(sample) ?? null;

    series.push({
      key,
      label: identity?.label ?? seriesLabel(key),
      // The mapping records a base byte and nothing about extent, which is
      // precisely why this source cannot produce an extent overlap and only
      // ever bounds on the wire model.
      size: 1,
      // 0 is the "category unknown" value `categoryKeyToMcNumber` already
      // returns, and it is the honest answer when nothing resolved. Falling
      // back to the raw `message_type` instead would not be a guess, it would
      // be a number in the wrong enum namespace wearing the right one's type.
      mcNumber: identity?.mcNumber ?? 0,
      regType: identity?.regType,
      // A matched column's verdict wins, an explicit `false` included — that is
      // an operator who turned the toggle off, and the matrix already flags on
      // it. Only a series with no column falls through to the data, which is
      // exactly the per-variant series this source exists for.
      sharedAcrossCars:
        identity?.sharedAcrossCars ?? offsets.every((offset) => offset === offsets[0]),
      addressAt: (unitIdx: number) => byUnit.get(unitIdx) ?? null,
    });
  });

  if (series.length === 0) return EMPTY_PROJECTION;

  return sweep(series, ceiling, []);
}

// ---------------------------------------------------------------------------
// Combining, deciding, describing
// ---------------------------------------------------------------------------

function dedupeZeroDeltaColumns(columns: ZeroDeltaColumn[]): ZeroDeltaColumn[] {
  const byKey = new Map<string, ZeroDeltaColumn>();
  columns.forEach((column) => {
    if (!byKey.has(column.colKey)) byKey.set(column.colKey, column);
  });

  return [...byKey.values()];
}

function isTighter(candidate: MaxCarsProjection, incumbent: MaxCarsProjection): boolean {
  if (candidate.firstCollidingCarCount === null) return false;
  if (incumbent.firstCollidingCarCount === null) return true;

  return candidate.firstCollidingCarCount < incumbent.firstCollidingCarCount;
}

/**
 * Fold several projections into the one an operator must obey.
 *
 * The TIGHTEST bound wins, because each source is a genuine constraint on its
 * own and satisfying only the looser one leaves the other's collision live. The
 * sources are never paired against EACH OTHER — the matrix and the mapping
 * describe the same registers in two notations, so cross-source pairs would
 * invent contests between a claim and its own restatement.
 */
export function combineMaxCarsProjections(...projections: MaxCarsProjection[]): MaxCarsProjection {
  return projections.reduce<MaxCarsProjection>((acc, next) => {
    const bounded = isTighter(next, acc) ? next : acc;
    const earlierExtent =
      acc.extentOverlap && next.extentOverlap
        ? [acc.extentOverlap, next.extentOverlap].sort((x, y) => x.carCount - y.carCount)[0]
        : (acc.extentOverlap ?? next.extentOverlap);

    return {
      bound: bounded.bound,
      firstCollidingCarCount: bounded.firstCollidingCarCount,
      safeCarCount: bounded.safeCarCount,
      extentOverlap: earlierExtent,
      // Deduped on colKey ALONE. The label is what an operator reads and two
      // sources may spell it differently for one register; the key is the only
      // thing they can be held to agreeing about, so it decides identity and
      // the first spelling seen supplies the words.
      zeroDeltaColumns: dedupeZeroDeltaColumns([...acc.zeroDeltaColumns, ...next.zeroDeltaColumns]),
    };
  }, EMPTY_PROJECTION);
}

/**
 * Where the value in the cap field came from.
 *
 * "unset" is the ONLY state auto-detection may write into, and it is deliberately
 * narrower than "the box is empty". An empty box has two very different
 * histories: nobody has ever expressed a cap, or the operator just cleared one.
 * A rule keyed on the VALUE alone cannot tell those apart, and the first version
 * of this was — so clearing the field re-populated it instantly, and the empty
 * state the tooltip promises was unreachable on any adapter whose offsets project
 * a collision. Provenance separates them, so clearing sticks.
 */
export type MaxCarsOrigin = "unset" | "stored" | "auto" | "manual";

/**
 * May auto-detect write this projection into the cap field?
 *
 * Two independent refusals, and both are reachable:
 *
 *   - anything but "unset" — a stored cap, a cap this already filled, or a
 *     number (or deliberate blank) the operator just entered. This is what makes
 *     "auto never clobbers a human" structural rather than a rule someone has to
 *     remember, and it makes the effect idempotent: after the first fill the
 *     origin is "auto", so a projection re-run by an unrelated edit leaves it be;
 *   - a cap below MIN_AUTO_FILL_CARS — see that constant. A projection of one
 *     car is a defect report, not a cap, and writing it would demolish a
 *     multi-car site from a background effect.
 */
export function shouldAutoFillMaxCars(
  origin: MaxCarsOrigin,
  projection: MaxCarsProjection,
): boolean {
  if (origin !== "unset") return false;

  return projection.safeCarCount !== null && projection.safeCarCount >= MIN_AUTO_FILL_CARS;
}

/**
 * How many cars the overlap analysis should actually enumerate.
 *
 * THE CAP IS PART OF THE QUESTION, not a filter applied to the answer. A cap of
 * N states that cars N+1 and beyond will never be generated, so a collision that
 * first appears at car N+1 is not a defect the operator can act on — it is a
 * collision between cars this adapter has been told not to build.
 *
 * Reporting it anyway is what made a capped adapter look permanently broken. On
 * a real RVU site Energize Car Call ran (2101, delta 3) and Secure Car Call ran
 * (2119, delta 3); the seventh Energize car lands on 2101 + 6*3 = 2119, which is
 * Secure's FIRST car. The operator set Max Cars precisely to stop expansion
 * short of that, and the report went on naming cars 7 and 8 — the two the cap
 * had just excluded — so the one action that resolves the collision looked like
 * it had done nothing.
 *
 * `bankLiftCount` still wins over `units` when the form was given a bank,
 * because the bank knows its own lifts while `units` is an artefact of how the
 * stored mapping happens to be shaped. The cap then bounds whichever won.
 *
 * A cap of zero or less is not a cap: 0 is the wire's no-cap sentinel and a
 * negative is not expressible in the field, so both leave the count alone rather
 * than collapsing the analysis to nothing.
 *
 * ONLY A COMMITTED CAP BOUNDS THE ANALYSIS, and the provenance is what says so.
 * An "auto" cap was derived FROM the first collision and sits one car below it,
 * so bounding by it would delete from the report the exact finding that produced
 * it — auto-detection would silence its own evidence, and an operator who never
 * pressed anything would be shown a clean report over an adapter that is not.
 * It is a suggestion until saved, and the caption says as much ("Save to store
 * it"). A "stored" cap is what the server will actually enforce and a "manual"
 * one is the operator's own instruction; cars beyond either genuinely never get
 * generated, so a collision among them is not a defect anybody can act on.
 */
export function boundedAnalysisCarCount(
  bankLiftCount: number,
  units: number,
  maxCars: number | null,
  origin: MaxCarsOrigin,
): number {
  const enumerated = bankLiftCount > 0 ? bankLiftCount : units;

  if (origin === "auto") return enumerated;

  if (maxCars === null || maxCars <= 0) return enumerated;

  return Math.min(enumerated, maxCars);
}

/**
 * Render a witness the way an operator reads the offset matrix, counting cars
 * from 1 the way the rest of the UI and the Go-side collision report do.
 */
export function describeCarCollision(witness: CarCollisionWitness): string {
  return (
    `byte ${witness.byte} — ${witness.a.label} car ${witness.a.unitIdx + 1} ` +
    `vs ${witness.b.label} car ${witness.b.unitIdx + 1}`
  );
}

/** Render whichever kind of bound stopped expansion, in the operator's terms. */
export function describeMaxCarsBound(bound: MaxCarsBound): string {
  if (bound.kind === "out-of-range" && bound.address) {
    return (
      `${bound.address.label} car ${bound.address.unitIdx + 1} lands on byte ` +
      `${bound.address.absByte}, which is outside the wire's address range`
    );
  }

  return bound.witness ? describeCarCollision(bound.witness) : `car ${bound.carCount}`;
}
